import { inject, Injectable } from '@angular/core';
import { Observable, of, map, forkJoin, catchError, finalize } from 'rxjs';
import {
    EnterpriseCropYearLocation,
    MapYearLocation,
    RoleCropYearLocation,
    RoleYearLocation,
} from '@/app/core/interfaces/cropmap-access.interface';
import {
    AccessEnterpriseProxy,
    AccessEnterpriseViewModel,
    AccessRoleProxy,
    AccessRoleViewModel,
    CountryAddressHierarchyViewModel,
    CountryHierarchyViewModel,
    CropMapLocationCountryResponse,
    LookupProxy,
} from '../../proxy/proxies';

/** Provided on the parent `cropmap` route with its API proxies (`cropmap-routes.ts`). */
@Injectable()
export class CropMapAccessService {
    private readonly accessEnterpriseProxy = inject(AccessEnterpriseProxy);
    private readonly accessRoleProxy = inject(AccessRoleProxy);
    private readonly lookupProxy = inject(LookupProxy);

    enterpriseCropYearLocations: EnterpriseCropYearLocation[] = [];
    authorizedCropYearLocations: RoleCropYearLocation[] = [];

    getEnterpriseAndRoleAccess(enterpriseId: string, roleId?: string): Observable<boolean> {
        if (!enterpriseId || !roleId) return of(false);

        this.resetAll();
        return forkJoin([
            this.accessEnterpriseProxy.accessEnterprise_GetByEnterpriseId(enterpriseId),
            this.accessRoleProxy.accessRole_GetByEnterpriseId(enterpriseId, roleId),
        ]).pipe(
            map(([enterpriseAccessRes, roleResponse]) => {
                const enterpriseAccessList = enterpriseAccessRes?.result ?? [];
                const accessRoles = roleResponse?.result ?? [];
                if (!enterpriseAccessList.length) return false;

                for (const enterpriseAccess of enterpriseAccessList) {
                    this.addEnterpriseCropYearLocation(
                        enterpriseAccess,
                        this.enterpriseCropYearLocations,
                    );
                }

                for (const role of accessRoles) {
                    this.addRoleCropYearLocation(role, this.authorizedCropYearLocations);
                }

                this.compareEnterpriseAndAuthorizedLocations();
                return true;
            }),
            catchError((error) => {
                console.error('CropMapAccessService: getEnterpriseAndRole error', error);
                return of(false);
            }),
        );
    }

    private addEnterpriseCropYearLocation(
        access: AccessEnterpriseViewModel,
        target: EnterpriseCropYearLocation[],
    ): void {
        if (!access.cropId || !access.seasonId) return;

        const cropIdx = target.findIndex((row) => row.cropId == access.cropId);
        if (cropIdx == -1) {
            target.push({
                cropId: access.cropId,
                cropName: (access.cropName as string) ?? '',
                cropColor: (access.cropColor as string) ?? '',
                seasonId: access.seasonId,
                seasonName: (access.season as string) ?? '',
                yearLocations: [
                    {
                        year: access.year,
                        countries: [{ id: access.countryId, name: (access.countryName as string) ?? '' }],
                    },
                ],
            });
        } else {
            const yearIdx = target[cropIdx].yearLocations.findIndex((yl) => yl.year == access.year);
            const countrySummary = { id: access.countryId, name: (access.countryName as string) ?? '' };
            if (yearIdx == -1) {
                target[cropIdx].yearLocations.push({ year: access.year, countries: [countrySummary] });
            } else {
                const countries = target[cropIdx].yearLocations[yearIdx].countries;
                if (!countries.some((c) => c.id == countrySummary.id)) {
                    countries.push(countrySummary);
                }
            }
        }
    }

    private addRoleCropYearLocation(
        access: AccessRoleViewModel,
        target: RoleCropYearLocation[],
    ): void {
        if (!access.cropId || !access.seasonId) return;

        const incomingHierarchy = access.countriesHierarchy ?? [];
        const cropIdx = target.findIndex((row) => row.cropId == access.cropId);
        if (cropIdx == -1) {
            target.push({
                cropId: access.cropId,
                cropName: access.cropName ?? '',
                cropColor: access.cropColor ?? '',
                seasonId: access.seasonId,
                seasonName: access.seasonName ?? '',
                yearLocations: [{ year: access.year, locations: structuredClone(incomingHierarchy) }],
            });
            return;
        }

        const yearIdx = target[cropIdx].yearLocations.findIndex((yl) => yl.year == access.year);
        if (yearIdx == -1) {
            target[cropIdx].yearLocations.push({
                year: access.year,
                locations: structuredClone(incomingHierarchy),
            });
        } else {
            target[cropIdx].yearLocations[yearIdx].locations = this.mergeCountryHierarchies(
                target[cropIdx].yearLocations[yearIdx].locations,
                incomingHierarchy,
            );
        }
    }

    private mergeCountryHierarchies(
        base: CountryHierarchyViewModel[],
        incoming: CountryHierarchyViewModel[],
    ): CountryHierarchyViewModel[] {
        const merged = structuredClone(base);
        for (const inc of incoming) {
            const idx = merged.findIndex((c) => c.countryId == inc.countryId);
            if (idx === -1) {
                merged.push(structuredClone(inc));
                continue;
            }
            const existing = merged[idx];
            for (const r of inc.regions ?? []) {
                if (!(existing.regions ?? []).some((x) => x.regionId == r.regionId)) {
                    (existing.regions = existing.regions ?? []).push(structuredClone(r));
                }
            }
            for (const s of inc.states ?? []) {
                if (!(existing.states ?? []).some((x) => x.stateId == s.stateId)) {
                    (existing.states = existing.states ?? []).push(structuredClone(s));
                }
            }
        }
        return merged;
    }

    private hierarchyToMapCountries(
        hierarchy: CountryHierarchyViewModel[] | undefined,
    ): CropMapLocationCountryResponse[] {
        if (!hierarchy?.length) return [];
        const countries: CropMapLocationCountryResponse[] = [];
        for (const hc of hierarchy) {
            const c = new CropMapLocationCountryResponse();
            (c as any).countryId = hc.countryId;
            c.name = hc.name;
            c.regions = (hc.regions ?? [])
                .map((r) => ({
                    regionId: r.regionId,
                    name: r.name,
                    cities: (r.cities ?? [])
                        .filter((ci) => ci.districts?.length)
                        .map((ci) => ({
                            cityId: ci.cityId,
                            name: ci.name,
                            districts: ci.districts!.map((d) => ({ districtId: d.districtId, name: d.name })),
                        })),
                }))
                .filter((r) => r.cities.length > 0) as any;
            c.states = (hc.states ?? [])
                .map((s) => ({
                    stateId: s.stateId,
                    name: s.name,
                    cities: (s.cities ?? [])
                        .filter((ci) => ci.districts?.length)
                        .map((ci) => ({
                            cityId: ci.cityId,
                            name: ci.name,
                            districts: ci.districts!.map((d) => ({ districtId: d.districtId, name: d.name })),
                        })),
                }))
                .filter((s) => s.cities.length > 0) as any;
            if ((c.regions?.length ?? 0) > 0 || (c.states?.length ?? 0) > 0) {
                countries.push(c);
            }
        }
        return countries;
    }

    private enterpriseRefsToCountries(
        countries: { id: string; name: string }[],
    ): CropMapLocationCountryResponse[] {
        return countries.map((c) => {
            const m = new CropMapLocationCountryResponse();
            (m as any).countryId = c.id;
            m.name = c.name;
            return m;
        });
    }

    private mergeCountriesIntoBucket(
        bucket: CropMapLocationCountryResponse[],
        incoming: CropMapLocationCountryResponse[],
    ): void {
        for (const country of incoming) {
            const cid = (country as any).countryId;
            const idx = bucket.findIndex((e) => (e as any).countryId == cid);
            if (idx == -1) {
                bucket.push(country);
                continue;
            }
            const existing = bucket[idx];
            const regions = country.regions ?? [];
            const states = country.states ?? [];
            if (regions.length > 0) {
                const er = existing.regions ?? [];
                existing.regions = er;
                for (const r of regions) {
                    const ri = er.findIndex((x) => (x as any).regionId == (r as any).regionId);
                    if (ri == -1) er.push(r);
                    else {
                        const ec = er[ri].cities ?? [];
                        er[ri].cities = ec;
                        for (const city of r.cities ?? []) {
                            if (!ec.some((x) => (x as any).cityId == (city as any).cityId)) ec.push(city);
                        }
                    }
                }
            }
            if (states.length > 0) {
                const es = existing.states ?? [];
                existing.states = es;
                for (const s of states) {
                    const si = es.findIndex((x) => (x as any).stateId == (s as any).stateId);
                    if (si == -1) es.push(s);
                    else {
                        const ec = es[si].cities ?? [];
                        es[si].cities = ec;
                        for (const city of s.cities ?? []) {
                            if (!ec.some((x) => (x as any).cityId == (city as any).cityId)) ec.push(city);
                        }
                    }
                }
            }
        }
    }

    private idEq(left: unknown, right: unknown): boolean {
        return String(left) === String(right);
    }

    getSeasons(type: 'enterprise' | 'authorized' = 'enterprise'): Array<{ id: string; name: string }> {
        const rows = type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const seen = new Set<string>();
        const out: Array<{ id: string; name: string }> = [];
        for (const r of rows) {
            const k = String(r.seasonId);
            if (!seen.has(k)) {
                seen.add(k);
                out.push({ id: r.seasonId, name: r.seasonName });
            }
        }
        return out.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    getCropsBySeasonId(
        seasonIds: string[],
        type: 'enterprise' | 'authorized' = 'enterprise',
    ): Array<{ id: string; name: string; color?: string; seasonId: string }> {
        const rows = type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const out: Array<{ id: string; name: string; color?: string; seasonId: string }> = [];
        const seen = new Set<string>();
        for (const r of rows) {
            const key = `${r.seasonId}:${r.cropId}`;
            if (
                seasonIds.some((s) => this.idEq(s, r.seasonId)) &&
                !seen.has(key)
            ) {
                seen.add(key);
                out.push({
                    id: r.cropId,
                    name: r.cropName,
                    color: r.cropColor,
                    seasonId: r.seasonId,
                });
            }
        }
        return out.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    getYearLocationByCropSeason(
        cropIds: string[],
        type: 'enterprise' | 'authorized' = 'enterprise',
        seasonId?: string,
    ): MapYearLocation[] {
        const years: MapYearLocation[] = [];
        const rows = type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const matchCrop = (c: string) => cropIds.some((cid) => this.idEq(cid, c));
        const matchSeason = (s: string) =>
            seasonId == null || this.idEq(s, seasonId);

        const matching = rows.filter(
            (r) => matchCrop(r.cropId) && matchSeason(r.seasonId),
        );
        for (const r of matching) {
            for (const yl of r.yearLocations) {
                const mapCountries =
                    type === 'enterprise'
                        ? this.enterpriseRefsToCountries((yl as any).countries ?? [])
                        : this.hierarchyToMapCountries((yl as RoleYearLocation).locations);
                let bucket = years.find((y) => y.year === yl.year);
                if (!bucket) {
                    bucket = { year: yl.year, locations: [] };
                    years.push(bucket);
                }
                this.mergeCountriesIntoBucket(bucket.locations, mapCountries);
            }
        }
        return years.sort((a, b) => b.year - a.year);
    }

    getYearLocationsForSeasonAndYear(
        seasonId: string,
        year: number,
        cropIds?: string[],
        type: 'enterprise' | 'authorized' = 'enterprise',
    ): MapYearLocation | null {
        const rows = type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const filtered = rows.filter(
            (r) =>
                this.idEq(r.seasonId, seasonId) &&
                (!cropIds?.length || cropIds.some((c) => this.idEq(c, r.cropId))),
        );
        if (!filtered.length) return null;

        const merged: MapYearLocation = { year, locations: [] };
        for (const r of filtered) {
            const yl = r.yearLocations.find((y) => y.year == year);
            if (!yl) continue;
            const slice =
                type === 'enterprise'
                    ? this.enterpriseRefsToCountries((yl as any).countries ?? [])
                    : this.hierarchyToMapCountries((yl as RoleYearLocation).locations);
            this.mergeCountriesIntoBucket(merged.locations, slice);
        }
        return merged.locations.length > 0 ? merged : null;
    }

    getCountriesBySeasonAndYear(
        seasonId: string,
        year: number,
        cropIds?: string[],
        type: 'enterprise' | 'authorized' = 'enterprise',
    ): CropMapLocationCountryResponse[] {
        const yl = this.getYearLocationsForSeasonAndYear(seasonId, year, cropIds, type);
        if (!yl) return [];
        const countries: CropMapLocationCountryResponse[] = [];
        for (const c of yl.locations) {
            const idx = countries.findIndex((e) => String((e as any).countryId) === String((c as any).countryId));
            if (idx === -1) countries.push(c);
            else this.mergeCountriesIntoBucket([countries[idx]], [c]);
        }
        return countries;
    }

    enrichCountriesWithHierarchy(
        flatCountries: { id: string; name: string }[],
    ): Observable<CropMapLocationCountryResponse[]> {
        if (!flatCountries.length) return of([]);
        const calls = flatCountries.map((c) =>
            this.lookupProxy.lookup_GetCountryAddressHierarchy(c.id).pipe(
                map((res) => this.addressHierarchyToCountry(c.id, c.name, res?.result)),
                catchError(() => {
                    const f = new CropMapLocationCountryResponse();
                    (f as any).countryId = c.id;
                    f.name = c.name;
                    return of(f);
                }),
            ),
        );
        return forkJoin(calls);
    }

    private addressHierarchyToCountry(
        countryId: string,
        countryName: string,
        h: CountryAddressHierarchyViewModel | undefined,
    ): CropMapLocationCountryResponse {
        const c = new CropMapLocationCountryResponse();
        (c as any).countryId = countryId;
        c.name = countryName;
        if (h?.states?.length) {
            c.states = h.states.map((node) => ({
                stateId: node.key,
                name: node.value,
                cities: (node.cities ?? []).map((ci) => ({
                    cityId: ci.key,
                    name: ci.value,
                    districts: (ci.districts ?? []).map((d) => ({ districtId: d.key, name: d.value })),
                })),
            })) as any;
        }
        if (h?.regions?.length) {
            c.regions = h.regions.map((node) => ({
                regionId: node.key,
                name: node.value,
                cities: (node.cities ?? []).map((ci) => ({
                    cityId: ci.key,
                    name: ci.value,
                    districts: (ci.districts ?? []).map((d) => ({ districtId: d.key, name: d.value })),
                })),
            })) as any;
        }
        return c;
    }

    private compareEnterpriseAndAuthorizedLocations(): void {
        this.authorizedCropYearLocations = this.authorizedCropYearLocations.filter((auth) => {
            const ent = this.enterpriseCropYearLocations.find((e) => e.cropId == auth.cropId);
            if (!ent) return false;
            auth.yearLocations = auth.yearLocations.filter((authYl) => {
                const entYl = ent.yearLocations.find((e) => e.year == authYl.year);
                if (!entYl) return false;
                const allowed = new Set(((entYl as any).countries ?? []).map((c: any) => c.id));
                if (allowed.size === 0) return true;
                (authYl as RoleYearLocation).locations = (authYl as RoleYearLocation).locations.filter(
                    (h) => allowed.has(h.countryId),
                );
                return (authYl as RoleYearLocation).locations.length > 0;
            });
            return auth.yearLocations.length > 0;
        });
    }

    resetAll(): void {
        this.enterpriseCropYearLocations = [];
        this.authorizedCropYearLocations = [];
    }

    clear(): void {
        this.resetAll();
    }
}
